import { useEffect, useState } from 'react'
import { AlertCircle, Link, Plus, Wrench } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ImageUpload } from '@/components/ui/image-upload'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { DifficultyIndicator } from '@/components/recipe/difficulty-indicator'
import { getEquipmentIcon, KITCHEN_EQUIPMENT } from '@/lib/equipment'
import { MEAL_TYPES } from '@/lib/meal-types'
import { aiRecipeImage } from '@/services/ai-recipe-image'
import type { RecipeFormData } from '@/types/recipe'

interface RecipeFormMetadataProps {
  mode: 'create' | 'edit'
  errors: Record<string, unknown>
  imageUploadMode: boolean
  isSubmitting: boolean
  onImageUploadModeChange: (enabled: boolean) => void
  generateAiImage: boolean
  onGenerateAiImageChange: (enabled: boolean) => void
}

export function RecipeFormMetadata({
  mode,
  errors,
  imageUploadMode,
  isSubmitting,
  onImageUploadModeChange,
  generateAiImage,
  onGenerateAiImageChange,
}: RecipeFormMetadataProps) {
  const { register, setValue, watch } = useFormContext<RecipeFormData>()
  const [platformAIConfigured, setPlatformAIConfigured] = useState<boolean | null>(aiRecipeImage.peekStatus())

  useEffect(() => {
    void aiRecipeImage.getStatus().then(setPlatformAIConfigured)
  }, [])

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Basisinformatie</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Titel *</label>
            <Input
              {...register('title')}
              placeholder="Bijvoorbeeld: Klassieke Spaghetti Carbonara"
              className={errors.title ? 'border-red-500' : ''}
            />
            {typeof errors.title === 'object' && errors.title && 'message' in errors.title ? (
              <p className="mt-1 text-sm text-red-500">{String(errors.title.message)}</p>
            ) : null}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Beschrijving</label>
            <Textarea {...register('description')} placeholder="Een korte beschrijving van het gerecht..." rows={3} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-900">
            <label className="flex items-start gap-3 text-sm text-gray-900 dark:text-slate-100">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                {...register('is_public')}
              />
              <span>
                Zichtbaar maken voor iedereen op het platform
                <span className="mt-1 block text-gray-600 dark:text-slate-300">
                  Handig als je wilt dat anderen dit recept kunnen vinden in Zoeken en gebruiken voor Bring-import.
                </span>
              </span>
            </label>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Receptafbeelding</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onImageUploadModeChange(false)}
                  className={`inline-flex items-center rounded-md border px-3 py-2 text-sm ${
                    !imageUploadMode ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <Link className="mr-1 h-4 w-4" />
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => onImageUploadModeChange(true)}
                  className={`inline-flex items-center rounded-md border px-3 py-2 text-sm ${
                    imageUploadMode ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Upload
                </button>
              </div>
            </div>

            {imageUploadMode ? (
              <ImageUpload
                onImageUploaded={(imageUrl) => setValue('image_url', imageUrl)}
                onImageRemoved={() => setValue('image_url', '')}
                existingImages={watch('image_url') ? [watch('image_url')] : []}
                maxImages={1}
                disabled={isSubmitting}
              />
            ) : (
              <Input
                {...register('image_url')}
                placeholder="https://example.com/image.jpg"
                className={errors.image_url ? 'border-red-500' : ''}
              />
            )}

            {typeof errors.image_url === 'object' && errors.image_url && 'message' in errors.image_url ? (
              <p className="mt-1 text-sm text-red-500">{String(errors.image_url.message)}</p>
            ) : null}

            {mode === 'create' ? (
              <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
                <label className="flex items-start gap-3 text-sm text-orange-900">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    checked={generateAiImage}
                    onChange={(event) => onGenerateAiImageChange(event.target.checked)}
                    disabled={isSubmitting || platformAIConfigured === false}
                  />
                    <span>
                    Voeg bij het opslaan ook een receptfoto toe
                    <span className="mt-1 block text-orange-800">
                      Dit staat standaard uit. Zet het alleen aan als je wilt dat de app zelf een passende foto maakt voor dit recept.
                    </span>
                  </span>
                </label>

                {platformAIConfigured === false ? (
                  <div className="mt-3 flex items-start gap-2 text-sm text-amber-800">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>Deze hulp werkt pas nadat de beheerder de slimme functies heeft ingeschakeld.</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Receptdetails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Bereidingstijd (min)</label>
              <Input type="number" {...register('prep_time', { valueAsNumber: true })} min="0" />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Kooktijd (min)</label>
              <Input type="number" {...register('cook_time', { valueAsNumber: true })} min="0" />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Porties</label>
              <Input type="number" {...register('servings', { valueAsNumber: true })} min="1" max="50" />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Moeilijkheidsgraad</label>
              <select {...register('difficulty')} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Selecteer...</option>
                <option value="gemakkelijk">Gemakkelijk</option>
                <option value="middel">Middel</option>
                <option value="moeilijk">Moeilijk</option>
              </select>
              <div className="mt-2">
                <DifficultyIndicator difficulty={watch('difficulty')} />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Maaltijdtype</label>
              <select {...register('meal_type')} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Selecteer...</option>
                {MEAL_TYPES.map((mealType) => (
                  <option key={mealType.key} value={mealType.key}>
                    {mealType.icon} {mealType.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Rating (1-5)</label>
            <Input type="number" {...register('rating', { valueAsNumber: true })} min="1" max="5" className="w-24" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Keukenapparatuur
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Selecteer de keukenapparatuur die nodig is voor dit recept.</p>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
              {KITCHEN_EQUIPMENT.map((item) => {
                const isSelected = watch('equipment').includes(item)
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      const currentEquipment = watch('equipment')
                      const newEquipment = isSelected
                        ? currentEquipment.filter((equipment) => equipment !== item)
                        : [...currentEquipment, item]

                      setValue('equipment', newEquipment)
                    }}
                    className={`rounded-lg border p-3 text-left transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getEquipmentIcon(item)}</span>
                      <span className="text-sm font-medium">{item}</span>
                    </div>
                  </button>
                )
              })}
            </div>

            {watch('equipment').length > 0 ? (
              <div className="mt-4">
                <h4 className="mb-2 text-sm font-medium text-gray-700">Geselecteerde apparatuur:</h4>
                <div className="flex flex-wrap gap-2">
                  {watch('equipment').map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-700"
                    >
                      <span>{getEquipmentIcon(item)}</span>
                      <span>{item}</span>
                      <button
                        type="button"
                        onClick={() => setValue('equipment', watch('equipment').filter((equipment) => equipment !== item))}
                        className="ml-1 hover:text-primary-900"
                      >
                        <Plus className="h-3 w-3 rotate-45" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Extra notities</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea {...register('notes')} placeholder="Tips, variaties of andere opmerkingen..." rows={3} />
        </CardContent>
      </Card>
    </>
  )
}
